'use client';

import { ScrollArea } from '@mds/ui';
import { Box, styled, SwipeableDrawer, SwipeableDrawerProps } from '@mui/material';
import { appBarHeightCompact, appBarHeightWide } from '../AppBarLayout/AppBarLayout';
import { useAppLayout } from '../AppLayoutProvider';
import { NavigationItemProvider } from '../NavigationItem/context';

export const defaultSidebarWidth = 300;

const StyledSwipeableDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  flexShrink: 0,
  gap: theme.spacing(1),

  [`& .MuiDrawer-paper`]: {
    overflow: 'hidden',
    boxSizing: 'border-box',
    paddingTop: `var(--app-bar-size, ${appBarHeightCompact}px)`,
    [theme.breakpoints.up('sm')]: {
      paddingTop: `var(--app-bar-size, ${appBarHeightWide}px)`,
    },
  },

  variants: [
    {
      props: { anchor: 'left' },
      style: {
        width: `var(--app-sidebar-width, ${defaultSidebarWidth}px)`,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(0.5),
        zIndex: theme.zIndex.appSidebar,
        [`& .MuiDrawer-paper`]: {
          width: `var(--app-sidebar-width, ${defaultSidebarWidth}px)`,
          zIndex: theme.zIndex.appSidebar,
        },
      },
    },
    {
      props: { anchor: 'right' },
      style: {
        width: `var(--app-right-sidebar-width, ${defaultSidebarWidth}px)`,
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(1),
        zIndex: theme.zIndex.appRightSidebar,
        [`& .MuiDrawer-paper`]: {
          width: `var(--app-right-sidebar-width, ${defaultSidebarWidth}px)`,
          zIndex: theme.zIndex.appRightSidebar,
        },
      },
    },
  ],
}));

export interface AppSidebarOwnProps {
  /**
   * Which side the sidebar opens from.
   * @default 'left'
   */
  anchor?: 'left' | 'right';
  /**
   * Sidebar content scrolls by default.
   *
   * If the content is more complex, e.g. has some sticky sections, set this flag to true
   * and use `<ScrollArea>` to configure scrollable sections manually.
   */
  disableScrollArea?: boolean;
}

export type AppSidebarProps = Omit<SwipeableDrawerProps, 'open' | 'onClose' | 'onOpen'> & AppSidebarOwnProps;

/**
 * App sidebar. Opens from the left by default; pass `anchor="right"` for a right sidebar.
 *
 * Typically used with DashboardLayout or ScrollableBodyLayout.
 *
 * App sidebar remains mounted when closed by default.
 */
export function AppSidebar({ children, disableScrollArea, anchor = 'left', ...rest }: AppSidebarProps) {
  const {
    appBarSize,
    sidebarVariant,
    isSidebarOpen,
    toggleSidebar,
    rightSidebarVariant,
    isRightSidebarOpen,
    toggleRightSidebar,
  } = useAppLayout();

  const isRight = anchor === 'right';
  const variant = isRight ? rightSidebarVariant : sidebarVariant;
  const isOpen = isRight ? isRightSidebarOpen : isSidebarOpen;
  const toggle = isRight ? toggleRightSidebar : toggleSidebar;

  return (
    <StyledSwipeableDrawer
      anchor={anchor}
      variant={variant}
      open={isOpen}
      onClose={() => toggle(false)}
      onOpen={() => toggle(true)}
      ModalProps={{
        keepMounted: true, // better open performance on mobile
      }}
      {...rest}
      style={{
        ...(appBarSize ? { '--app-bar-size': `${appBarSize}px` } : {}),
        ...rest.style,
      }}
    >
      <NavigationItemProvider orientation="vertical">
        {disableScrollArea && children}

        {!disableScrollArea && (
          <ScrollArea.Root sx={{ flex: 1, overflow: 'hidden', width: '100%' }}>
            <ScrollArea.Viewport>
              <ScrollArea.Content>
                <Box>{children}</Box>
              </ScrollArea.Content>
            </ScrollArea.Viewport>
          </ScrollArea.Root>
        )}
      </NavigationItemProvider>
    </StyledSwipeableDrawer>
  );
}
